import React, { useState, useCallback, useEffect } from 'react';
import {
  ResponsiveContainer, ComposedChart, AreaChart, Area, Bar, BarChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, LabelList,
} from 'recharts';
import api from '../api/axios';
import {
  CHART, fmtINR, fmtNum, useLivePoll, LivePulse, KpiTile, ChartCard, CosmicTooltip, LiveClock, DeltaBadge,
} from '../components/analytics/kit';
import DrillDown, { DrillHint } from '../components/analytics/DrillDown';

// ════════════════════════════════════════════════════════════════════════════
//  SANCTUM ANALYTICS — deep multi-factor, per-centre, real-time (12s poll).
//  Backed by /admin/analytics/platform: pure MongoDB aggregations, zero mock.
//  Categorical palette is CVD-validated for the dark surface (fixed order).
// ════════════════════════════════════════════════════════════════════════════
const STATUS_COLOR = {
  Ordered: CHART.gold, Notified: CHART.gold, Packed: '#C9995B',
  Shipped: CHART.violet, 'Out for Delivery': CHART.violet, 'Driver Picked': CHART.violet,
  Delivered: CHART.tulsi, Cancelled: '#B3474F', Returned: '#B3474F',
};

const PlatformAnalytics = () => {
  const [d, setD] = useState(null);
  // which chart segment is open in the drill-down modal: {type, key} or null
  const [drill, setDrill] = useState(null);

  const fetchAll = useCallback(async () => {
    const res = await api.get('/admin/analytics/platform');
    setD(res.data?.data || null);
  }, []);
  const { ago, refresh } = useLivePoll(fetchAll, 10000);

  // REALTIME PUSH: the backend emits `analytics_event` the instant an order or donation
  // lands — refresh immediately instead of waiting for the next poll tick.
  useEffect(() => {
    const socket = window.socket;
    if (!socket) return;
    const onEvent = () => refresh();
    socket.on('analytics_event', onEvent);
    socket.on('order_status_updated', onEvent);
    return () => { socket.off('analytics_event', onEvent); socket.off('order_status_updated', onEvent); };
  }, [refresh]);

  if (!d) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gold/20 border-t-gold"></div>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-muted">Computing the Sacred Economy…</p>
        </div>
      </div>
    );
  }

  const feeSpark = d.monthly.map(m => ({ v: m.platformFee }));
  const devoteeSeries = d.monthly.map(m => ({ month: m.month, 'New Devotees': m.newDevotees, Orders: m.orderCount }));
  const centreStack = d.perCentre.map(t => ({
    name: t.name.length > 14 ? t.name.slice(0, 13) + '…' : t.name,
    Donations: t.donations, Offerings: t.offerings, 'Store Orders': t.orders, total: t.total, live: t.isLive,
    centreId: t.centreId,
  }));
  // recharts chart-level click → open the segment the cursor was on
  const chartDrill = (pick) => (e) => {
    const p = e?.activePayload?.[0]?.payload;
    const next = p && pick(p);
    if (next) setDrill(next);
  };

  return (
    <div className="min-h-screen p-6 md:p-8">
      {/* ── Header ── */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-1.5 text-[10px] font-black uppercase tracking-[0.3em] text-gold-deep">Ananda Marga.ai · Deep Intelligence</p>
          <h1 className="font-display text-3xl font-bold italic text-ivory md:text-4xl">
            Platform <span style={{ background: 'linear-gradient(100deg,#F6DC96,#E0B355 45%,#B8893C)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }} className="not-italic">Analytics</span>
          </h1>
          <p className="mt-1 text-[11px] font-semibold text-muted">Every figure is a live MongoDB aggregation — fee model: orders 10% · donations 5% · offerings 8% · <span className="text-gold">click any chart to drill inside it</span></p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <LiveClock />
          <LivePulse ago={ago} />
          <button onClick={refresh} className="glass-card flex items-center gap-2 rounded-xl px-4 py-2 text-[11px] font-black uppercase tracking-widest text-gold transition-colors hover:text-gold-light">
            <span className="material-symbols-outlined text-[16px]">refresh</span> Sync
          </button>
        </div>
      </div>

      {/* ── KPI row: platform economics ── */}
      <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-5 stagger-children">
        <KpiTile icon="account_balance" label="Aggregator Fees" value={d.platformFees.total} money accent={CHART.gold} spark={feeSpark} />
        <KpiTile icon="payments" label="Gross Volume (GMV)" value={d.gmv} money accent={CHART.violet} spark={d.monthly.map(m => ({ v: m.revenue }))} />
        <KpiTile icon="account_balance_wallet" label="Payouts Due" value={d.payoutsDue} money accent={CHART.tulsi} />
        <KpiTile icon="shopping_bag" label="Avg Order Value" value={d.aov} money accent={CHART.lotus} />
        <KpiTile icon="sensors" label="Centres Live" value={d.counts.liveCentres} suffix={` / ${d.counts.centres}`} accent={CHART.gold} />
      </div>

      {/* ── Row: TODAY'S PULSE (24h hourly) + Sacred Event Stream ── */}
      <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-3 stagger-children">
        <ChartCard className="xl:col-span-2" title="Today's Pulse · 24 Hours"
          subtitle="Hourly cashflow — updates the instant an order or donation lands (socket push)"
          right={<div className="flex items-center gap-3"><DrillHint /><LivePulse ago={ago} /></div>}>
          <div className="h-56 cursor-pointer">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.todayPulse || []} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}
                onClick={chartDrill(p => p.hourKey && { type: 'hour', key: p.hourKey })}>
                <defs>
                  <linearGradient id="pulseDon" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.gold} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={CHART.gold} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="pulseOrd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.violet} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={CHART.violet} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="hour" tick={{ fill: CHART.axis, fontSize: 9, fontWeight: 700 }} axisLine={false} tickLine={false} interval={2} />
                <YAxis tick={{ fill: CHART.axis, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={36} />
                <Tooltip content={<CosmicTooltip />} cursor={{ stroke: 'rgba(246,220,150,.35)', strokeWidth: 1 }} />
                <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} iconType="plainline" />
                <Area type="monotone" dataKey="donations" name="Donations" stroke={CHART.gold} strokeWidth={2} fill="url(#pulseDon)" dot={false} activeDot={{ r: 4 }} />
                <Area type="monotone" dataKey="orders" name="Store Orders" stroke={CHART.violet} strokeWidth={2} fill="url(#pulseOrd)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Sacred Event Stream" subtitle="Latest real activity across the platform">
          <div className="max-h-56 space-y-2 overflow-y-auto pr-1 custom-scrollbar">
            {(d.recentEvents || []).map((e, i) => (
              <div key={`${e.at}-${i}`} className="flex items-center gap-3 rounded-xl border border-[rgba(229,192,107,.10)] bg-white/[0.03] px-3 py-2 transition-colors hover:bg-white/[0.06]">
                <span className="material-symbols-outlined text-[18px]" style={{ color: e.kind === 'donation' ? CHART.gold : CHART.violet }}>
                  {e.kind === 'donation' ? 'volunteer_activism' : 'shopping_bag'}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11.5px] font-bold text-ivory">{e.label}</p>
                  <p className="truncate text-[9.5px] font-semibold text-muted">{e.centre || '—'} · {new Date(e.at).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })}</p>
                </div>
                <span className="shrink-0 text-[11.5px] font-black tabular-nums text-gold-light">{fmtINR(e.amount)}</span>
              </div>
            ))}
            {(!d.recentEvents || d.recentEvents.length === 0) && (
              <p className="py-8 text-center text-[11px] font-bold uppercase tracking-widest text-muted">Awaiting first event…</p>
            )}
          </div>
        </ChartCard>
      </div>

      {/* ── Row: per-centre economy (stacked) + fee composition ── */}
      <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-3 stagger-children">
        <ChartCard className="xl:col-span-2" title="Centre-wise Sacred Economy"
          subtitle="Donations · offerings · store orders per shrine (gross, ₹) — click a shrine to open its full economy"
          right={<div className="flex items-center gap-3"><DrillHint /><LivePulse ago={ago} /></div>}>
          <div className="cursor-pointer" style={{ height: Math.max(260, centreStack.length * 52) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={centreStack} layout="vertical" margin={{ top: 4, right: 76, bottom: 0, left: 0 }} barSize={20}
                onClick={chartDrill(p => p.centreId && { type: 'centre', key: p.centreId })}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" tick={{ fill: '#E5DCC4', fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} width={118} />
                <Tooltip content={<CosmicTooltip />} cursor={{ fill: 'rgba(246,220,150,.05)' }} />
                <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} />
                <Bar dataKey="Donations" stackId="a" fill={CHART.gold} stroke="#150E2E" strokeWidth={2} />
                <Bar dataKey="Offerings" stackId="a" fill={CHART.tulsi} stroke="#150E2E" strokeWidth={2} />
                <Bar dataKey="Store Orders" stackId="a" fill={CHART.violet} stroke="#150E2E" strokeWidth={2} radius={[0, 6, 6, 0]}>
                  <LabelList dataKey="total" position="right" formatter={(v) => fmtINR(v)} style={{ fill: '#F4EBD8', fontSize: 11, fontWeight: 800 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Aggregator Fee Streams" subtitle={`Total ${fmtINR(d.platformFees.total)} retained`} right={<DrillHint />}>
          <div className="h-52 cursor-pointer">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { name: 'Orders 10%', value: d.platformFees.fromOrders, streamKey: 'orders' },
                { name: 'Donations 5%', value: d.platformFees.fromDonations, streamKey: 'donations' },
                { name: 'Offerings 8%', value: d.platformFees.fromOfferings, streamKey: 'offerings' },
              ]} layout="vertical" margin={{ top: 4, right: 64, bottom: 0, left: 0 }} barSize={20}
                onClick={chartDrill(p => p.streamKey && { type: 'stream', key: p.streamKey })}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" tick={{ fill: '#E5DCC4', fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} width={104} />
                <Tooltip content={<CosmicTooltip />} cursor={{ fill: 'rgba(246,220,150,.05)' }} />
                <Bar dataKey="value" name="Fees" radius={[0, 6, 6, 0]}>
                  {[CHART.violet, CHART.gold, CHART.tulsi].map((c, i) => <Cell key={i} fill={c} />)}
                  <LabelList dataKey="value" position="right" formatter={(v) => fmtINR(v)} style={{ fill: '#F4EBD8', fontSize: 11, fontWeight: 800 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 rounded-xl border border-[rgba(229,192,107,.14)] bg-white/[0.03] p-3.5">
            <p className="flex items-center justify-between text-[11px] font-bold text-stone-300">
              <span>Payouts due to centres & vendors</span>
              <span className="tabular-nums text-gold-light">{fmtINR(d.payoutsDue)}</span>
            </p>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${(d.platformFees.total / (d.gmv || 1)) * 100}%`, background: 'linear-gradient(90deg,#F6DC96,#B8893C)' }} />
            </div>
            <p className="mt-1.5 text-[9px] font-black uppercase tracking-widest text-muted">
              take-rate {((d.platformFees.total / (d.gmv || 1)) * 100).toFixed(1)}% of GMV
            </p>
          </div>
        </ChartCard>
      </div>

      {/* ── Row: monthly multi-factor + growth ── */}
      <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-3 stagger-children">
        <ChartCard className="xl:col-span-2" title="Monthly Sacred Cashflows"
          subtitle="Stacked revenue streams with aggregator-fee line (₹, single axis) — click a month for its day-by-day story"
          right={<div className="flex items-center gap-3"><DrillHint /><DeltaBadge series={d.monthly.map(m => m.revenue)} /></div>}>
          <div className="h-80 cursor-pointer">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={d.monthly} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}
                onClick={chartDrill(p => p.monthKey && { type: 'month', key: p.monthKey })}>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="month" tick={{ fill: CHART.axis, fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: CHART.axis, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={40} />
                <Tooltip content={<CosmicTooltip />} cursor={{ fill: 'rgba(246,220,150,.05)' }} />
                <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} />
                <Bar dataKey="donations" name="Donations" stackId="m" fill={CHART.gold} stroke="#150E2E" strokeWidth={2} />
                <Bar dataKey="offerings" name="Offerings" stackId="m" fill={CHART.tulsi} stroke="#150E2E" strokeWidth={2} />
                <Bar dataKey="orders" name="Store Orders" stackId="m" fill={CHART.violet} stroke="#150E2E" strokeWidth={2} radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="platformFee" name="Aggregator Fee" stroke={CHART.lotus} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Devotee Growth" subtitle="New registrations vs orders per month (count)">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={devoteeSeries} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="gDev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.tulsi} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={CHART.tulsi} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gOrd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.violet} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={CHART.violet} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="month" tick={{ fill: CHART.axis, fontSize: 10, fontWeight: 700 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: CHART.axis, fontSize: 10 }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<CosmicTooltip money={false} />} cursor={{ stroke: 'rgba(246,220,150,.35)', strokeWidth: 1 }} />
                <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} iconType="plainline" />
                <Area type="monotone" dataKey="New Devotees" stroke={CHART.tulsi} strokeWidth={2} fill="url(#gDev)" dot={false} activeDot={{ r: 4 }} />
                <Area type="monotone" dataKey="Orders" stroke={CHART.violet} strokeWidth={2} fill="url(#gOrd)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      {/* ── Row: fulfilment funnel + payment integrity + top products ── */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3 stagger-children">
        <ChartCard title="Fulfilment Funnel" subtitle="Order lifecycle — live counts by stage" right={<DrillHint />}>
          <div className="h-64 cursor-pointer">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.funnel} margin={{ top: 18, right: 8, bottom: 0, left: 0 }} barSize={30}
                onClick={chartDrill(p => p.status && { type: 'status', key: p.status })}>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="status" tick={{ fill: CHART.axis, fontSize: 9, fontWeight: 800 }} axisLine={false} tickLine={false} interval={0} angle={-18} textAnchor="end" height={44} />
                <YAxis hide />
                <Tooltip content={<CosmicTooltip money={false} />} cursor={{ fill: 'rgba(246,220,150,.05)' }} />
                <Bar dataKey="count" name="Orders" radius={[5, 5, 0, 0]}>
                  {d.funnel.map((f, i) => <Cell key={i} fill={STATUS_COLOR[f.status] || CHART.gold} />)}
                  <LabelList dataKey="count" position="top" style={{ fill: '#F4EBD8', fontSize: 11, fontWeight: 800 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Payment Integrity" subtitle="Captured vs pending order value" right={<DrillHint />}>
          <div className="space-y-4 pt-2">
            {d.paymentMix.map((p) => {
              const totalAmt = d.paymentMix.reduce((s, x) => s + x.amount, 0) || 1;
              const pct = (p.amount / totalAmt) * 100;
              const paid = p.status === 'PAID';
              return (
                <div key={p.status} role="button" tabIndex={0} className="cursor-pointer rounded-lg p-1 -m-1 transition-colors hover:bg-white/[0.03]"
                  onClick={() => setDrill({ type: 'payment', key: p.status === 'PENDING' ? 'PENDING' : 'PAID' })}
                  onKeyDown={(e) => e.key === 'Enter' && setDrill({ type: 'payment', key: p.status === 'PENDING' ? 'PENDING' : 'PAID' })}>
                  <div className="mb-1.5 flex items-center justify-between text-[11px] font-bold">
                    <span className="flex items-center gap-2 text-stone-300">
                      <span className="material-symbols-outlined text-[15px]" style={{ color: paid ? CHART.tulsi : '#B3474F' }}>{paid ? 'verified' : 'schedule'}</span>
                      {paid ? 'PAID (verified)' : 'PENDING'} · {fmtNum(p.count)} orders
                    </span>
                    <span className="tabular-nums text-ivory">{fmtINR(p.amount)}</span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, background: paid ? `linear-gradient(90deg, ${CHART.tulsi}, #35C285)` : 'linear-gradient(90deg,#B3474F,#D96A88)' }} />
                  </div>
                </div>
              );
            })}
            <p className="pt-2 text-[10px] font-semibold leading-relaxed text-muted">
              Payment status is authoritative — only Razorpay-captured or wallet-debited orders count as PAID; PENDING orders can never ship or fund payouts.
            </p>
          </div>
        </ChartCard>

        <ChartCard title="Top Sacred Products" subtitle="By captured revenue (units in label)" right={<DrillHint />}>
          <div className="space-y-3 pt-1">
            {d.topProducts.slice(0, 6).map((p, i) => {
              const max = d.topProducts[0]?.revenue || 1;
              return (
                <div key={p.name} role="button" tabIndex={0} className="cursor-pointer rounded-lg p-1 -m-1 transition-colors hover:bg-white/[0.03]"
                  onClick={() => setDrill({ type: 'product', key: p.name })}
                  onKeyDown={(e) => e.key === 'Enter' && setDrill({ type: 'product', key: p.name })}>
                  <div className="mb-1 flex items-center justify-between gap-3 text-[11px] font-bold">
                    <span className="truncate text-stone-300">{i + 1}. {p.name}</span>
                    <span className="shrink-0 tabular-nums text-gold-light">{fmtINR(p.revenue)} <span className="text-muted font-semibold">· {p.units}u</span></span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${(p.revenue / max) * 100}%`, background: `linear-gradient(90deg, ${CHART.categorical[i % 4]}, ${CHART.categorical[i % 4]}cc)` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>
      </div>

      {/* ── drill-down modal (opens on any chart click) ── */}
      <DrillDown drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
};

export default PlatformAnalytics;
